import { useState } from 'react';
import { Box, Button, Divider, Modal, Text } from '@hopskipdrive/react-components';
import { startOfMinute } from 'date-fns';

import { colorPalette } from '../../../../../shared/components/variables';
import { useToast } from '../../../../../shared/contexts/ToastContext';
import { useTimestampUpdates } from '../../../../hooks/useTimestampUpdates';
import { RideOverviewDetails } from '../../../../types/rideOverview';
import { TimestampsFormType } from '../../../../types/timestamps';

import { EditTimestampForm } from './EditTimestampForm';
import { editTimestampValidationSchema } from './editTimestampValidationSchema';

interface EditTimestampModalProps {
  rideOverviewDetails: RideOverviewDetails;
  rideSlug: string | undefined;
  onClose: () => void;
}

export const EditTimestampModal: React.FC<EditTimestampModalProps> = ({ rideOverviewDetails, rideSlug, onClose }) => {
  const { addToast } = useToast();
  const { mutate, isPending } = useTimestampUpdates({
    rideSlug: rideSlug!,
    successCallback: () => {
      onClose();
    },
    errorCallback: (
      error: Error & {
        errors: string[];
      }
    ) => {
      error?.errors
        ? error?.errors?.forEach((errorMessage) => {
            addToast({
              severity: 'error',
              message: errorMessage,
            });
          })
        : addToast({
            severity: 'error',
            message: error.message,
          });
    },
  });
  const [showTimestampRemovalModal, setShowTimestampRemovalModal] = useState(false);
  const [formData, setFormData] = useState<TimestampsFormType>();

  const onSubmit = (data: TimestampsFormType) => {
    setFormData(data);
    const shouldModalBeShown = checkDeletedTimestamps(rideOverviewDetails, data);

    if (!shouldModalBeShown) {
      validateForm(data);
    }
  };

  const validateForm = (data) => {
    editTimestampValidationSchema()
      .validate(data)
      .then(() => {
        const isoTimestamps = {
          actualCheckedInAt: data.actualCheckedInAt
            ? startOfMinute(new Date(data.actualCheckedInAt)).toISOString()
            : '',
          actualStartsAt: data.actualStartsAt ? startOfMinute(new Date(data.actualStartsAt)).toISOString() : '',
          actualEndsAt: data.actualEndsAt ? startOfMinute(new Date(data.actualEndsAt)).toISOString() : '',
          waypointsAttributes: data.waypointsAttributes.map((waypoint) => ({
            ...waypoint,
            actualArrivesAt: waypoint.actualArrivesAt
              ? startOfMinute(new Date(waypoint.actualArrivesAt)).toISOString()
              : '',
            actualDepartsAt: waypoint.actualDepartsAt
              ? startOfMinute(new Date(waypoint.actualDepartsAt)).toISOString()
              : '',
          })),
        };

        mutate(isoTimestamps);
      });
  };

  const checkDeletedTimestamps = (initialValues, submitValues) => {
    if (
      (initialValues.checkInDetails.actualCheckedInAt && !submitValues.actualCheckedInAt) ||
      (initialValues.actualStartsAt && !submitValues.actualStartsAt) ||
      (initialValues.actualEndsAt && !submitValues.actualEndsAt)
    ) {
      setShowTimestampRemovalModal(true);

      return true;
    }

    initialValues.routeDetails.map((waypoint) => {
      const initialWaypoint = initialValues.routeDetails.find((w) => w.id === waypoint.id);
      const submitWaypoint = submitValues.waypointsAttributes.find((w) => w.id === waypoint.id);

      if (
        (initialWaypoint?.arrivalDetails.actual && !submitWaypoint?.actualArrivesAt) ||
        (initialWaypoint?.departureDetails.actual && !submitWaypoint?.actualDepartsAt)
      ) {
        setShowTimestampRemovalModal(true);

        return true;
      }

      setShowTimestampRemovalModal(false);

      return false;
    });

    setShowTimestampRemovalModal(false);

    return false;
  };

  return (
    <>
      <Modal onClose={onClose} open={true}>
        <Modal.Title>Edit Timestamp</Modal.Title>
        <Modal.Content>
          <EditTimestampForm rideOverviewDetails={rideOverviewDetails} onSubmit={onSubmit} />
        </Modal.Content>
        <Divider />
        <Modal.Actions>
          <Button color="tonal" disabled={isPending} onClick={onClose}>
            Never mind
          </Button>
          <Button color="primary" form="edit-timestamp-form" type="submit" loading={isPending}>
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Actions>
      </Modal>
      {showTimestampRemovalModal && (
        <TimestampRemovalModal
          onClose={() => setShowTimestampRemovalModal(false)}
          onConfirm={() => validateForm(formData)}
          isPending={isPending}
        />
      )}
    </>
  );
};

const TimestampRemovalModal = ({ onClose, onConfirm, isPending }) => {
  return (
    <Modal open={true}>
      <Modal.Title>Confirm changes?</Modal.Title>
      <Modal.Content>
        <Box padding="16px">
          <Text variant="body1" textColor="secondaryText">
            Automated messages are suppressed (except for CareDriver and vehicle information). Please reach out to
            caregiver and organizer as needed.
          </Text>
        </Box>
      </Modal.Content>
      <Divider />
      <Modal.Actions style={{ borderTop: `1px solid ${colorPalette.neutralVariant90}` }}>
        <Button variant="text" onClick={onClose} loading={isPending}>
          Never mind
        </Button>
        <Button color="primary" onClick={onConfirm} loading={isPending}>
          Ok
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
